import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation, useNavigate} from "react-router-dom";
import styled from "styled-components";
import {Family} from "./pages/family";
import {Games} from "./pages/games";
import {Events} from "./pages/events";
import {Trivia} from "./pages/trivia";
import {TapOrSkip} from "./pages/tapOrSkip";
import {SigalGear} from "./pages/sigalGear";
import {SigalArtPlan} from "./pages/sigalArtPlan";
import {SigalSchool} from "./pages/sigalSchool";
import {SigalHours} from "./pages/sigalHours";
import {Schedule} from "./pages/schedule";
import {About} from "./pages/about";
import {WHATSAPP_URL, glass} from "./ui";
import {useRouteMeta} from "./seo/useRouteMeta";


// SF-Symbols-like glyphs (24px grid, stroke = currentColor)
const Icon = ({children}) => (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const icons = {
    games: <Icon><path d="M7.5 7.5h9a4.5 4.5 0 0 1 4.4 5.4l-.8 4a2.6 2.6 0 0 1-4.5 1.2L13.9 16h-3.8l-1.7 2.1a2.6 2.6 0 0 1-4.5-1.2l-.8-4A4.5 4.5 0 0 1 7.5 7.5z"/><path d="M8 10.5v3M6.5 12h3"/><circle cx="15.5" cy="11" r=".6" fill="currentColor"/><circle cx="17" cy="13" r=".6" fill="currentColor"/></Icon>,
    family: <Icon><circle cx="12" cy="5" r="2.2"/><circle cx="5.5" cy="18.5" r="2.2"/><circle cx="18.5" cy="18.5" r="2.2"/><path d="M12 7.2v4.3M5.5 16.3v-2.3a2.5 2.5 0 0 1 2.5-2.5h8a2.5 2.5 0 0 1 2.5 2.5v2.3"/></Icon>,
    events: <Icon><rect x="3.5" y="5" width="17" height="15.5" rx="3"/><path d="M3.5 9.5h17M8 3v4M16 3v4"/><circle cx="12" cy="14.5" r="1" fill="currentColor"/></Icon>,
    sigal: <Icon><path d="M12 3.5a8.5 8.5 0 0 0 0 17c1.3 0 1.9-.8 1.9-1.7 0-1.3-1.2-1.6-1.2-2.8 0-1 .8-1.6 1.8-1.6h2.1a3.9 3.9 0 0 0 3.9-3.9c0-3.9-3.8-7-8.5-7z"/><circle cx="7.8" cy="11" r="1.1" fill="currentColor"/><circle cx="10.5" cy="7.4" r="1.1" fill="currentColor"/><circle cx="15" cy="7.6" r="1.1" fill="currentColor"/></Icon>,
    about: <Icon><circle cx="12" cy="12" r="8.8"/><circle cx="12" cy="10" r="3"/><path d="M6.4 18.6a6.5 6.5 0 0 1 11.2 0"/></Icon>,
};

// RTL: first item sits on the right
const TABS = [
    {to: "/about", label: "מי אני", icon: icons.about},
    {to: "/", end: true, label: "משחקים", icon: icons.games},
    {key: "family", label: "משפחה", icon: icons.family, children: [
        {to: "/family", label: "עץ משפחה", icon: icons.family},
        {to: "/events", label: "אירועי משפחה", icon: icons.events},
    ]},
    {to: "/sigal", label: "סיגל", icon: icons.sigal},
];

const tabKey = (t) => t.key || t.to;
const isActive = (t, path) => (t.children
    ? t.children.some((c) => c.to === path)
    : t.end ? path === t.to : path === t.to || path.startsWith(t.to + '/'));

// inline title shown in the phone's compact bar once the large title scrolls away
const PAGE_TITLES = {"/": "משחקים", "/events": "אירועי המשפחה", "/sigal": "סיגל בית ספר", "/about": "מי אני"};

const CONTACT_PAGES = ["/family"];   // full-canvas page; others end with an inline ContactFooter

export const App = () => (
    <Router>
        <AppContent/>
    </Router>
);

const AppContent = () => {
    const location = useLocation();
    const p = location.pathname;
    const hideChrome = p === '/trivia' || p === '/dilema' || p.startsWith('/sigal/') || p === '/schedule';
    const scroll = usePageScroll(p);
    useRouteMeta(p);

    return (
        <Main className={hideChrome ? 'no-chrome' : undefined}>
            {!hideChrome && <TopBar scrolled={scroll.scrolled}/>}
            {!hideChrome && PAGE_TITLES[p] && <CompactBar className={scroll.collapsed ? 'on' : undefined} aria-hidden={!scroll.collapsed}>{PAGE_TITLES[p]}</CompactBar>}
            {!hideChrome && <TabBar/>}
            <div className="route-enter" key={p}>
                <Routes location={location}>
                    <Route path="/" element={<Games/>}/>
                    <Route path="/family" element={<Family/>}/>
                    <Route path="/gallery" element={<Navigate to="/family" replace/>}/>
                    <Route path="/games" element={<Navigate to="/" replace/>}/>
                    <Route path="/dilema" element={<TapOrSkip/>}/>
                    <Route path="/events" element={<Events/>}/>
                    <Route path="/trivia" element={<Trivia/>}/>
                    <Route path="/sigal" element={<SigalSchool/>}/>
                    <Route path="/about" element={<About/>}/>
                    <Route path="/schedule" element={<Schedule/>}/>
                    <Route path="/sigal/gear" element={<SigalGear/>}/>
                    <Route path="/sigal/art-lesson-plan" element={<SigalArtPlan/>}/>
                    <Route path="/sigal/hours" element={<SigalHours/>}/>
                </Routes>
            </div>
            {!hideChrome && CONTACT_PAGES.includes(p) && (
                <ContactPill data-glass href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="מצאתם טעות? שלחו לי הודעה בוואטסאפ">
                    <WhatsAppGlyph/>
                    <span>מצאתם טעות?</span>
                </ContactPill>
            )}
        </Main>
    );
};

/** Tracks the active page's scroll (pages own their scroll container, `.page`). */
function usePageScroll(path) {
    const [state, setState] = useState({scrolled: false, collapsed: false});
    useEffect(() => {
        setState({scrolled: false, collapsed: false});
        let raf = 0;
        const onScroll = (e) => {
            const el = e.target;
            if (!el.classList || !el.classList.contains('page')) return;
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const y = el.scrollTop;
                const next = {scrolled: y > 2, collapsed: y > 64};
                setState((s) => (s.scrolled === next.scrolled && s.collapsed === next.collapsed ? s : next));
            });
        };
        document.addEventListener('scroll', onScroll, true);
        return () => { document.removeEventListener('scroll', onScroll, true); cancelAnimationFrame(raf); };
    }, [path]);
    return state;
}

/* ------------------------------------------------------------------ desktop: translucent top bar + segmented control */
const TopBar = ({scrolled}) => {
    const location = useLocation();
    const navRef = useRef(null);
    const [pill, setPill] = useState(null);

    useLayoutEffect(() => {
        const place = () => {
            const nav = navRef.current;
            const active = nav && nav.querySelector('[data-active="true"]');
            if (!active) { setPill(null); return; }
            // measure against the control itself: the grouped item sits inside its own anchor
            const n = nav.getBoundingClientRect(), r = active.getBoundingClientRect();
            setPill({x: r.left - n.left - nav.clientLeft, w: r.width});
        };
        place();
        window.addEventListener('resize', place);
        // fonts can load after first paint and change the widths
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(place);
        return () => window.removeEventListener('resize', place);
    }, [location.pathname]);

    return (
        <Bar className={scrolled ? 'scrolled' : undefined}>
            <Brand to="/about">כפיר מילר</Brand>
            <Segmented ref={navRef}>
                {pill && <SegPill style={{transform: `translateX(${pill.x}px)`, width: pill.w}}/>}
                {TABS.map((t) => t.children
                    ? <GroupMenu key={tabKey(t)} tab={t} active={isActive(t, location.pathname)} placement="below"/>
                    : <SegLink key={tabKey(t)} to={t.to} end={t.end} data-active={isActive(t, location.pathname)}>{t.label}</SegLink>)}
            </Segmented>
            <BarSpacer/>
        </Bar>
    );
};

/* ------------------------------------------------------------------ phone: iOS tab bar */
const TabBar = () => {
    const {pathname} = useLocation();
    const idx = TABS.findIndex((t) => isActive(t, pathname));
    return (
        <Tabs aria-label="ניווט ראשי">
            {idx >= 0 && <Lens style={{width: `calc((100% - 8px) / ${TABS.length})`, transform: `translateX(${-idx * 100}%)`}}/>}
            {TABS.map((t) => t.children
                ? <GroupMenu key={tabKey(t)} tab={t} active={isActive(t, pathname)} placement="above"/>
                : (
                    <TabLink key={tabKey(t)} to={t.to} end={t.end}>
                        <span className="glyph">{t.icon}</span>
                        <span className="lbl">{t.label}</span>
                    </TabLink>
                ))}
        </Tabs>
    );
};

/* ------------------------------------------------------------------ grouped item ("משפחה") with a menu anchored to it
   Desktop: drops down from the segment. Phone: rises from the tab. Either way it grows out of its trigger. */
const GroupMenu = ({tab, active, placement}) => {
    const [open, setOpen] = useState(false);
    const {pathname} = useLocation();
    const navigate = useNavigate();
    const ref = useRef(null);

    useEffect(() => { setOpen(false); }, [pathname]);
    useEffect(() => {
        if (!open) return;
        const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('pointerdown', away);
        document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
    }, [open]);

    const Trigger = placement === 'above' ? TabButton : SegButton;
    return (
        <MenuAnchor ref={ref} className={placement}>
            <Trigger type="button" data-active={active} aria-haspopup="menu" aria-expanded={open}
                     className={active ? 'active' : undefined} onClick={() => setOpen((o) => !o)}>
                {placement === 'above' && <span className="glyph">{tab.icon}</span>}
                <span className="lbl">{tab.label}</span>
                {placement === 'below' && <Caret className={open ? 'up' : undefined} aria-hidden="true" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4"/></Caret>}
            </Trigger>
            <Menu role="menu" className={`${placement}${open ? ' open' : ''}`} aria-hidden={!open}>
                {tab.children.map((c) => (
                    <MenuItem key={c.to} role="menuitem" type="button" tabIndex={open ? 0 : -1}
                              className={pathname === c.to ? 'current' : undefined}
                              onClick={() => { setOpen(false); navigate(c.to); }}>
                        <span className="ic">{c.icon}</span>
                        <span className="txt">{c.label}</span>
                        {pathname === c.to && <Check viewBox="0 0 14 11" aria-hidden="true"><path d="M1 5.5l4 4L13 1"/></Check>}
                    </MenuItem>
                ))}
            </Menu>
        </MenuAnchor>
    );
};

const WhatsAppGlyph = () => (
    <svg viewBox="0 0 32 32" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M16.004 0C7.164 0 .002 7.158.002 15.995c0 2.82.737 5.573 2.14 7.998L0 32l8.247-2.16a15.95 15.95 0 007.753 1.977h.004C24.84 31.817 32 24.66 32 15.995 32 7.158 24.844 0 16.004 0zm0 29.313a13.28 13.28 0 01-6.97-1.97l-.5-.297-5.19 1.36 1.384-5.06-.326-.518a13.22 13.22 0 01-2.028-7.05c0-7.34 5.98-13.312 13.326-13.312 7.345 0 13.326 5.972 13.326 13.312 0 7.34-5.98 13.536-13.022 13.536zm7.304-9.975c-.4-.2-2.367-1.168-2.734-1.3-.367-.134-.634-.2-.9.2-.268.4-1.034 1.3-1.268 1.568-.234.267-.467.3-.867.1-.4-.2-1.69-.623-3.22-1.987-1.19-1.062-1.993-2.374-2.227-2.774-.234-.4-.025-.616.176-.815.18-.18.4-.467.6-.7.2-.234.267-.4.4-.668.134-.267.067-.5-.033-.7-.1-.2-.9-2.168-1.234-2.968-.325-.78-.656-.674-.9-.686l-.767-.013c-.267 0-.7.1-1.067.5-.367.4-1.4 1.368-1.4 3.336 0 1.968 1.434 3.87 1.634 4.136.2.267 2.822 4.31 6.838 6.042.955.413 1.7.659 2.282.844.96.305 1.832.262 2.522.159.77-.115 2.367-.968 2.7-1.902.334-.935.334-1.735.234-1.902-.1-.168-.367-.267-.767-.467z"/></svg>
);

/* ------------------------------------------------------------------ styles */
const Main = styled.div`
    height: 100%;
    direction: rtl;

    &.no-chrome .page {
        padding-top: 0;
        padding-bottom: 0;
    }
`;

const material = `
    background: var(--material);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    backdrop-filter: saturate(180%) blur(20px);
    @media (prefers-reduced-transparency: reduce) {
        background: rgba(250, 250, 252, 0.98);
        -webkit-backdrop-filter: none;
        backdrop-filter: none;
    }
`;

const Bar = styled.header`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--topbar-h);
    z-index: 50;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    padding: 0 20px;
    /* scroll-edge effect: the material only appears once content slides under it */
    background: transparent;
    box-shadow: 0 0.5px 0 transparent;
    transition: background-color 240ms ease, box-shadow 240ms ease, -webkit-backdrop-filter 240ms ease, backdrop-filter 240ms ease;
    &.scrolled {
        ${material}
        box-shadow: 0 0.5px 0 var(--separator);
    }

    @media (max-width: 767px) {
        display: none;
    }
`;

/* phone: iOS inline title that takes over when the large title scrolls off */
const CompactBar = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    display: none;
    padding: calc(env(safe-area-inset-top, 0px) + 12px) 16px 12px;
    text-align: center;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--label);
    ${material}
    box-shadow: 0 0.5px 0 var(--separator);
    opacity: 0;
    transform: translateY(-6px);
    pointer-events: none;
    transition: opacity 220ms ease, transform 320ms var(--ease);

    &.on { opacity: 1; transform: none; }

    @media (max-width: 767px) {
        display: block;
    }
    @media (prefers-reduced-motion: reduce) {
        transform: none;
    }
`;

const Brand = styled(NavLink)`
    justify-self: start;
    font-size: 17px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--label);
    text-decoration: none;
`;

const BarSpacer = styled.div``;

const Segmented = styled.nav`
    position: relative;
    display: flex;
    padding: 4px;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.62), rgba(255, 255, 255, 0.34));
    -webkit-backdrop-filter: blur(18px) saturate(190%);
    backdrop-filter: blur(18px) saturate(190%);
    box-shadow:
        inset 0 1px 0.5px rgba(255, 255, 255, 0.9),
        inset 0 0 0 0.5px rgba(0, 0, 0, 0.06),
        0 8px 24px rgba(0, 0, 0, 0.08),
        0 1px 2px rgba(0, 0, 0, 0.06);
`;

/* the glass "lens" that slides under the active item */
const SegPill = styled.span`
    position: absolute;
    top: 4px;
    bottom: 4px;
    left: 0;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(0, 122, 255, 0.16), rgba(0, 122, 255, 0.1));
    box-shadow:
        inset 0 1px 0.5px rgba(255, 255, 255, 0.9),
        inset 0 0 0 0.5px rgba(0, 122, 255, 0.18),
        0 2px 8px rgba(0, 122, 255, 0.14);
    /* not gesture-driven: one critically damped curve, retargets from wherever it is */
    transition: transform 420ms var(--ease), width 420ms var(--ease);
    @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const SegLink = styled(NavLink)`
    position: relative;
    z-index: 1;
    padding: 8px 18px;
    border-radius: 999px;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--label);
    text-decoration: none;
    border-radius: 8px;
    transition: opacity 120ms ease;

    &[data-active='true'] { font-weight: 600; color: var(--tint); }
    &:active { opacity: 0.55; transition: none; }
`;

/* phone: a floating Liquid Glass capsule, detached from the screen edge */
const Tabs = styled.nav`
    position: fixed;
    left: 12px;
    right: 12px;
    bottom: calc(var(--safe-b) + 10px);
    z-index: 50;
    display: none;
    height: 62px;
    padding: 4px;
    box-sizing: border-box;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.46));
    -webkit-backdrop-filter: blur(22px) saturate(190%);
    backdrop-filter: blur(22px) saturate(190%);
    box-shadow:
        inset 0 1px 0.5px rgba(255, 255, 255, 0.95),
        inset 0 0 0 0.5px rgba(0, 0, 0, 0.06),
        0 12px 32px rgba(0, 0, 0, 0.14),
        0 2px 6px rgba(0, 0, 0, 0.06);
    @media (prefers-reduced-transparency: reduce) {
        background: #fff;
        -webkit-backdrop-filter: none;
        backdrop-filter: none;
    }

    @media (max-width: 767px) {
        display: flex;
    }
`;

const Lens = styled.span`
    position: absolute;
    top: 4px;
    bottom: 4px;
    right: 4px;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(0, 122, 255, 0.15), rgba(0, 122, 255, 0.08));
    box-shadow:
        inset 0 1px 0.5px rgba(255, 255, 255, 0.95),
        inset 0 0 0 0.5px rgba(0, 122, 255, 0.16);
    transition: transform 460ms var(--ease);
    pointer-events: none;
    @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const TabLink = styled(NavLink)`
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    color: rgba(60, 60, 67, 0.55);
    text-decoration: none;
    -webkit-user-select: none;
    user-select: none;

    .glyph { display: flex; transition: transform 160ms var(--ease); }
    .lbl { font-size: 10.5px; font-weight: 500; letter-spacing: 0.01em; }

    &[aria-current='page'] { color: var(--tint); }
    &[aria-current='page'] svg { stroke-width: 2.1; }
    /* feedback on touch-down, not release */
    &:active .glyph { transform: scale(0.86); transition-duration: 60ms; }
`;

const ContactPill = styled.a`
    position: fixed;
    z-index: 40;
    left: 16px;
    bottom: 16px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px 8px 12px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    ${glass}
    color: #1a9e4b;

    @media (max-width: 767px) {
        bottom: calc(var(--tabbar-h) + var(--safe-b) + 12px);
        left: 12px;
    }
`;

const MenuAnchor = styled.div`
    position: relative;
    display: flex;
    z-index: 1;

    &.above { flex: 1; }
`;

const SegButton = styled.button`
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 8px 18px;
    font-size: 15px;
    border: none;
    background: none;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--label);
    border-radius: 8px;
    cursor: pointer;
    transition: opacity 120ms ease;

    &.active { font-weight: 600; color: var(--tint); }
    &:active { opacity: 0.55; transition: none; }
`;

const Caret = styled.svg`
    width: 10px;
    height: 6px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: 0.6;
    transition: transform 320ms var(--ease);
    &.up { transform: rotate(180deg); }
`;

const TabButton = styled.button`
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    color: rgba(60, 60, 67, 0.55);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    .glyph { display: flex; transition: transform 160ms var(--ease); }
    .lbl { font-size: 10.5px; font-weight: 500; letter-spacing: 0.01em; }
    &.active { color: var(--tint); }
    &.active svg { stroke-width: 2.1; }
    &:active .glyph { transform: scale(0.86); transition-duration: 60ms; }
`;

/* materializes out of its trigger: scale + fade from the anchored origin, same path back */
const Menu = styled.div`
    position: absolute;
    left: 50%;
    min-width: 210px;
    padding: 6px;
    border-radius: 20px;
    background: rgba(252, 252, 253, 0.97);
    box-shadow: 0 0 0 0.5px var(--separator), 0 12px 32px rgba(0, 0, 0, 0.16), 0 2px 6px rgba(0, 0, 0, 0.06);
    opacity: 0;
    visibility: hidden;
    transition: opacity 160ms ease, transform 300ms var(--ease), visibility 0s linear 300ms;

    &.below {
        top: calc(100% + 10px);
        transform: translateX(-50%) scale(0.94);
        transform-origin: 50% 0;
    }
    &.above {
        bottom: calc(100% + 10px);
        transform: translateX(-50%) scale(0.94);
        transform-origin: 50% 100%;
    }
    &.open {
        opacity: 1;
        visibility: visible;
        transform: translateX(-50%) scale(1);
        transition: opacity 160ms ease, transform 300ms var(--ease), visibility 0s;
    }
    @media (prefers-reduced-motion: reduce) {
        &.below, &.above, &.open { transform: translateX(-50%); }
    }
`;

const MenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 44px;
    padding: 0 12px;
    border: none;
    border-radius: 14px;
    background: none;
    font: inherit;
    font-size: 15px;
    font-weight: 500;
    color: var(--label);
    text-align: right;
    cursor: pointer;
    white-space: nowrap;

    .ic { display: flex; color: var(--tint); }
    .ic svg { width: 20px; height: 20px; }
    .txt { flex: 1; }
    &.current { font-weight: 600; }
    @media (hover: hover) { &:hover { background: rgba(0, 122, 255, 0.08); } }
    &:active { background: rgba(0, 122, 255, 0.14); }
`;

const Check = styled.svg`
    width: 14px;
    height: 11px;
    fill: none;
    stroke: var(--tint);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
`;
